import { get, post } from './API';

/**
 * Admin-side review of expenses drivers submit from the operator portal
 * (parking tickets, other costs — with a receipt photo).
 *
 * Approving and filing the cost are ONE call: `orderLine` names the operator
 * the cost goes to and the API writes the ledger line and the decision
 * together, so an approved receipt cannot end up without its line. Omitting
 * it is the third outcome — absorbed — and is exactly what approving always
 * did. The line itself still goes through the one guarded entry point
 * (OrderLinesController.addAdjustment); nothing here writes order_lines.
 */
const DriverExpensesService = {
  // Returns the expense rows for one booking (ph-core toAdminExpenseView):
  // [{ _id, type: 'parking'|'other', amount, currency, note, booking_id,
  //    booking_reference, driver_id, operator_id, operator_name,
  //    submitted_by: { kind: 'driver'|'operator', name? },
  //    status: 'submitted'|'approved'|'rejected',
  //    cost_operator: { id, name, settlement_currency } | null,
  //    photo_url, created_at, reviewed_by, reviewed_at }]
  // `cost_operator` is the bucket the receipt's VEHICLE settles this kind of
  // cost against, derived server-side from the car's own cost_operators map —
  // null for a third-party vehicle, and the screen then pre-selects the
  // operator on the trip (`operator_id`) instead.
  // An operator login's own claim has no driver_id; submitted_by names the
  // operator instead.
  // `photo_url` is a presigned link that expires after ~15 minutes, so
  // callers should re-fetch rather than hold on to it.
  async listForBooking(bookingId) {
    const response = await get('operators/portal-expenses', { bookingId });
    // Enveloped as { success, data: { expenses: [...] } } by the API.
    const data = response?.data?.data ?? response?.data;
    const rows = Array.isArray(data) ? data : data?.expenses;
    return Array.isArray(rows) ? rows : [];
  },

  // decision: 'approved' | 'rejected'.
  // orderLine: { operator_id } to approve AND file the cost against that
  // operator; omitted to approve with no line (absorbed). Only the operator
  // is ever sent — the description, amount, currency, role and the link back
  // to the receipt are all derived server-side from the receipt itself.
  // Returns the updated expense, with `order_line` when one was written.
  async review(expenseId, decision, orderLine) {
    const response = await post('operators/portal-expenses', {
      expense_id: expenseId,
      decision,
      ...(orderLine ? { order_line: orderLine } : {}),
    });
    return response?.data?.data ?? response?.data;
  },
};

export default DriverExpensesService;
